import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, CheckCircle2 } from "lucide-react";
import useSeo from "@/lib/useSeo";
import ComingSoon from "@/components/ComingSoon";
import { IMAGES, EASY_MASSAGE_URL } from "@/lib/assets";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const benefits = [
  "Online booking with live availability",
  "Staff, roster & commission management",
  "Loyalty, memberships & prepaid credits",
  "Online card payments & fewer no-shows",
  "Your own web address or custom domain",
  "We handle the tech, hosting & updates",
];

export default function Home() {
  useSeo({
    title: "Easy Booking Solutions | Booking Websites That Work For Your Industry",
    description:
      "Easy Booking Solutions builds beautiful, fully-bookable websites for service businesses. Explore Easy Massage (live now) with Easy Detailing coming soon.",
    path: "/",
  });

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="px-6 md:px-12 pt-16 md:pt-24 pb-20">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <span className="text-xs md:text-sm font-bold tracking-[0.2em] uppercase text-[#3EA65B]">
              Booking software for service businesses
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-[#0A0A0A] mt-4">
              Easy Booking solutions for your industry that work.
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed mt-6 max-w-xl">
              We build and manage stunning, fully-bookable websites for service businesses —
              online booking, staff rostering, loyalty and payments in one simple platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-9">
              <a
                href={EASY_MASSAGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-button"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0A0A0A] text-white px-8 py-4 font-semibold hover:bg-gray-800 transition-all"
              >
                Explore Easy Massage <ArrowRight className="h-5 w-5" />
              </a>
              <Link
                to="/industries"
                data-testid="hero-industries-button"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-gray-200 text-[#0A0A0A] px-8 py-4 font-semibold hover:border-gray-900 transition-all"
              >
                See all industries
              </Link>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8">
              {["No code", "Live same day", "Cancel anytime"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle2 className="h-4 w-4 text-[#3EA65B]" /> {t}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-4 bg-[#F4FBF6] rounded-[2.5rem] -z-10" />
            <img
              src={IMAGES.hero}
              alt="Business owner managing online bookings on a laptop"
              className="w-full h-[380px] md:h-[480px] object-cover rounded-3xl shadow-xl"
            />
          </motion.div>
        </div>
      </section>

      {/* Live now: Easy Massage */}
      <section className="px-6 md:px-12 py-20 bg-[#F4FBF6]">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <span className="text-xs md:text-sm font-bold tracking-[0.2em] uppercase text-[#3EA65B]">
              Live now
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-[#0A0A0A] mt-4">
              One platform. Built for every service business.
            </h2>
          </div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center bg-white border border-gray-100 rounded-3xl overflow-hidden mt-12"
            data-testid="industry-card-easy-massage"
          >
            <div className="relative h-64 lg:h-[400px]">
              <img src={IMAGES.massage} alt="Easy Massage" className="w-full h-full object-cover" />
              <span className="absolute top-5 left-5 text-xs font-bold px-3 py-1.5 rounded-full bg-[#3EA65B] text-white">
                Live now
              </span>
            </div>
            <div className="p-8 lg:p-12">
              <h3 className="font-heading text-3xl font-bold text-[#0A0A0A]">Easy Massage</h3>
              <p className="text-lg text-gray-600 leading-relaxed mt-4">
                Beautiful, fully-bookable websites for massage studios & day spas — online booking,
                therapist rostering, loyalty rewards, memberships and payments in one platform.
              </p>
              <a
                href={EASY_MASSAGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="visit-easy-massage"
                className="inline-flex items-center gap-2 mt-8 rounded-full bg-[#0A0A0A] text-white px-8 py-4 font-semibold hover:bg-gray-800 transition-all"
              >
                Visit the site <ArrowUpRight className="h-5 w-5" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Coming soon */}
      <ComingSoon />

      {/* Why */}
      <section className="px-6 md:px-12 py-24">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <span className="text-xs md:text-sm font-bold tracking-[0.2em] uppercase text-[#3EA65B]">
              Why Easy Booking
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-[#0A0A0A] mt-4">
              Everything your clients need to book. Nothing you don't.
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mt-5">
              A polished, self-serve dashboard to run your whole business — while we handle the
              tech, hosting and updates so you can focus on your customers.
            </p>
            <a
              href={EASY_MASSAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="why-cta"
              className="inline-flex items-center gap-2 mt-8 rounded-full bg-[#3EA65B] text-white px-8 py-4 font-semibold hover:bg-[#328C4C] transition-all"
            >
              See it in action <ArrowRight className="h-5 w-5" />
            </a>
          </motion.div>

          <motion.ul
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 gap-x-8 gap-y-5"
          >
            {benefits.map((b, i) => (
              <motion.li key={b} custom={i} variants={fadeUp} className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-[#3EA65B] shrink-0 mt-0.5" />
                <span className="text-gray-700">{b}</span>
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* CTA band */}
      <section className="px-6 md:px-12 pb-24">
        <div className="max-w-7xl mx-auto bg-[#0A0A0A] rounded-[2rem] px-8 md:px-16 py-16 text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-white tracking-tight">
            Ready for a website that books itself?
          </h2>
          <p className="text-gray-300 text-lg mt-4 max-w-xl mx-auto">
            Get your business online today — booking, loyalty and memberships included.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-9">
            <a
              href={EASY_MASSAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-band-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#0A0A0A] px-8 py-4 font-semibold hover:bg-gray-100 transition-all"
            >
              Get my website <ArrowRight className="h-5 w-5" />
            </a>
            <Link
              to="/contact"
              data-testid="footer-band-contact"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-gray-700 text-white px-8 py-4 font-semibold hover:border-white transition-all"
            >
              Talk to us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
